import { motion, type Variants } from "framer-motion";
import { Link } from "wouter";
import { ArrowRight, Truck, ShieldAlert } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

import serviceGaragentore from '@assets/leistungen/garagentore/garagentore.jpg';
import serviceIndustrietore from '@assets/leistungen/industrietore/industrietore.jpeg';
import serviceBrandschutz from '@assets/leistungen/brandschutz/tueren.jpeg';
import serviceUvv from '@assets/leistungen/uvv/uvv.jpg';
import serviceMetallbauGelaender from '@assets/leistungen/metallbau/gelaender-mit-bus.webp';
import serviceEinbruchschutz from '@assets/leistungen/einbruchschutz/fenstergitter.jpeg';
import serviceVerladebruecken from '@assets/leistungen/verladebruecken/verladebruecken.jpeg';

type Service = {
  title: string;
  desc: string;
  route: string;
  img?: string;
  imgPosition?: string;
  icon?: React.ReactNode;
};

const allServices: Service[] = [
  {
    title: "Garagentore",
    desc: "Verkauf, Montage, Wartung und Reparatur von Sektional-, Schwing- und Rolltoren für private und gewerbliche Garagen",
    route: "/garagentore",
    img: serviceGaragentore,
  },
  {
    title: "Industrietore",
    desc: "Professionelle Lösungen für Industrie und Gewerbe, von Sektional- und Rolltoren bis hin zu Schiebetoren",
    route: "/industrietore",
    img: serviceIndustrietore,
  },
  {
    title: "Brandschutztüren und -tore",
    desc: "Verkauf, Montage, Wartung und Reparatur von zugelassenen Brandschutzelementen nach aktuellen Normen und Vorschriften",
    route: "/brandschutztueren-tore",
    img: serviceBrandschutz,
  },
  {
    title: "UVV-Prüfungen",
    desc: "Wiederkehrende Prüfungen nach ASR A1.7 an Türen, Toren und Verladebrücken",
    route: "/uvv-pruefung",
    img: serviceUvv,
  },
  {
    title: "Metallbauarbeiten",
    desc: "Individuelle Metallbauarbeiten, auch aus Edelstahl V2A",
    route: "/metallbauarbeiten",
    img: serviceMetallbauGelaender,
  },
  {
    title: "Einbruchschutz",
    desc: "Hochwertige Sicherheitslösungen für Ihr Zuhause oder Ihren Betrieb",
    route: "/einbruchschutz",
    img: serviceEinbruchschutz,
  },
  {
    title: "Verladebrücken",
    desc: "Verkauf, Wartung, Reparatur und Prüfung von Verladebrücken für einen sicheren Betrieb",
    route: "/verladebruecken",
    img: serviceVerladebruecken,
    imgPosition: "center bottom",
  },
];

const fadeUpVariant = {
  hidden: { opacity: 0, y: 25 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
} satisfies Variants;

export default function LeistungenPage() {
  return (
    <div>
      {/* Header */}
      <section className="bg-zinc-50 py-16 md:py-24 border-b border-zinc-100">
        <div className="container mx-auto px-4 md:px-6 text-center max-w-3xl">
          <motion.p
            className="text-primary font-bold tracking-wider uppercase text-sm mb-3"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Leistungsübersicht
          </motion.p>
          <motion.h1
            className="text-3xl md:text-5xl font-bold text-zinc-900 mb-5 leading-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.1 }}
          >
            Unsere Leistungen
          </motion.h1>
          <motion.p
            className="text-zinc-600 text-lg leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.2 }}
          >
            Wir bieten Ihnen ein umfassendes Leistungsspektrum rund um Türen, Tore und Sicherheitstechnik.
          </motion.p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-28 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {allServices.map((service, index) => (
              <motion.div
                key={index}
                className="group"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-40px" }}
                variants={{
                  hidden: { opacity: 0, y: 25 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: index * 0.07 } },
                }}
              >
                <Link href={service.route} className="block h-full">
                  <Card className="h-full border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-lg bg-white overflow-hidden flex flex-col cursor-pointer">
                    {service.img ? (
                      <div className="w-full h-48 overflow-hidden shrink-0">
                        <img
                          src={service.img}
                          alt={service.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          style={{ objectPosition: service.imgPosition }}
                        />
                      </div>
                    ) : (
                      <div className="w-full h-48 bg-zinc-50 flex items-center justify-center shrink-0 border-b border-border/30">
                        {service.icon}
                      </div>
                    )}
                    <CardContent className="p-6 flex flex-col flex-1">
                      <h3 className="text-lg font-bold mb-2 text-zinc-900">{service.title}</h3>
                      <p className="text-zinc-500 text-sm leading-relaxed flex-1">{service.desc}</p>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <motion.div
            className="mt-16 md:mt-20 text-center bg-zinc-50 rounded-2xl p-10 md:p-14 border border-zinc-100"
            initial={fadeUpVariant.hidden}
            whileInView={fadeUpVariant.visible}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-zinc-900 mb-4">
              Haben Sie Fragen zu unseren Leistungen?
            </h2>
            <p className="text-zinc-600 mb-8 text-lg max-w-xl mx-auto">
              Wir beraten Sie gerne persönlich und erstellen ein individuelles Angebot für Ihr Projekt.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/"
                className="inline-flex items-center justify-center gap-2 bg-primary text-white font-semibold px-8 py-3 rounded-full hover:bg-primary/90 transition-colors"
              >
                Jetzt Kontakt aufnehmen <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:0794133514"
                className="inline-flex items-center justify-center gap-2 border border-primary text-primary font-semibold px-8 py-3 rounded-full hover:bg-primary/5 transition-colors"
              >
                07941 33 51 4
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
